import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import QueryDropdown from "components/QueryDropdown";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import { usePermissions } from "shared/hooks/usePermissions";
import {
    COLUMN_RESOURCES,
    DROPDOWN_SIZES,
    ENDPOINTS,
    ID_TYPE
} from "shared/types/enums";
import { Query } from "shared/types/interfaces";

import {
    CompanyAndGroupDropdownsProps,
    DeviceData,
    DROPDOWN_NAME
} from "./types";

export const CompanyAndGroupDropdowns = ({
    isLoading,
    isCompanyRequired,
    isRootRequired,
    isGroupNotAllowed,
    clearData,
    setPayload
}: CompanyAndGroupDropdownsProps) => {
    const { t } = useTranslation();
    const { PERMISSIONS, getNoPermissionTooltipTitle } = usePermissions();

    const [device, setDevice] = useState<DeviceData>({
        [DROPDOWN_NAME.Company]: null,
        [DROPDOWN_NAME.Group]: null
    });

    const [resourceGroup, setResourceGroup] = useState("");

    const { company, group } = device;

    useEffect(() => {
        const isReady = isCompanyRequired ? company : company || group;

        if (isReady) {
            const data = {
                company: company ? company.id : null,
                group: !group || group.id === -1 ? null : group.id
            };

            setPayload(data);
        } else {
            setPayload(null);
        }
        // eslint-disable-next-line
    }, [isCompanyRequired, company, group]);

    const handleChange = useCallback(
        (dropdownValue: Query | null, dropdownName: DROPDOWN_NAME) => {
            const companyInfo = dropdownValue?.groupCompanyInfo;

            if (dropdownName === DROPDOWN_NAME.Company) {
                setDevice({
                    [DROPDOWN_NAME.Company]: dropdownValue,
                    [DROPDOWN_NAME.Group]: null
                });
            } else {
                const isCompanyEmpty =
                    dropdownValue?.id &&
                    dropdownValue.id !== -1 &&
                    company === null;

                setDevice({
                    ...device,
                    [dropdownName]: dropdownValue,
                    ...(isCompanyEmpty && {
                        [DROPDOWN_NAME.Company]: {
                            id: companyInfo!.companyId,
                            name: companyInfo!.parentName
                        }
                    })
                });
            }

            const getGroupValue = () => {
                if (dropdownName === DROPDOWN_NAME.Company) {
                    return dropdownValue === null
                        ? ENDPOINTS.Groups
                        : `${ENDPOINTS.Groups}?company_id=${dropdownValue.id}`;
                } else {
                    return dropdownValue === null || dropdownValue.id === -1
                        ? resourceGroup
                        : `${ENDPOINTS.Groups}?company_id=${companyInfo?.companyId}`;
                }
            };

            setResourceGroup(getGroupValue());
        },

        [company, device, resourceGroup]
    );

    const getTooltipTitle = () => {
        if (isGroupNotAllowed) {
            return getNoPermissionTooltipTitle(PERMISSIONS.DeviceGroupRead);
        }

        if (!company) {
            return t("Dialog##field disabled until company selected");
        }

        return undefined;
    };

    return (
        <div
            css={css({
                marginBottom: "16px",

                "& > div + div": {
                    marginTop: "16px"
                }
            })}
        >
            <CompanyDropdown
                data-testid="device-company-query-dropdown"
                value={company}
                change={(_, val) => handleChange(val, DROPDOWN_NAME.Company)}
                customStyle={{ marginTop: "16px" }}
                isRequired={isCompanyRequired}
                isRootRequired={isRootRequired}
                readOnly={isLoading}
            />

            {clearData}

            <QueryDropdown
                data-testid="device-group-query-dropdown"
                resource={resourceGroup}
                idType={ID_TYPE.Id}
                value={group}
                hasNotAssigned={!isCompanyRequired}
                size={DROPDOWN_SIZES.Medium}
                fullWidth
                change={(_, val) => handleChange(val, DROPDOWN_NAME.Group)}
                labelLeft={t("Table##group")}
                textFieldParams={{
                    placeholder: t("Dialog##select")
                }}
                disabled={isGroupNotAllowed || !company}
                hasNoPermission={isGroupNotAllowed}
                readOnly={isLoading}
                tooltipTitle={getTooltipTitle()}
                description={{
                    resource: COLUMN_RESOURCES.Id,
                    name: t("General##id")
                }}
            />
        </div>
    );
};

export default CompanyAndGroupDropdowns;
